import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import DataTable from '../../components/Table/DataTable';
import SearchBox from '../../components/SearchBox/SearchBox';

import { getShortLinkDomains, updateShortLinkDomain, deleteShortLinkDomain } from '../../services/shortLinkDomains';
import { showAlert } from '../../store/ducks/alert';

import { CircularProgress } from '@material-ui/core';
import { Button, InputField, Modal, Text } from '@getpingback/realeza';
import { Edit as EditIcon } from '@material-ui/icons';
import { TrashCanIcon } from '@stash-ui/regular-icons';

const headers = ['id', 'channelUid', 'domain', 'type', 'status', 'distributionStatus', 'createdAt', 'deletedAt'];

const ShortLinkDomains = () => {
  const dispatch = useDispatch();

  const [shortLinkDomains, setShortLinkDomains] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isOrdering, setIsOrdering] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [searchField, setSearchField] = useState({ searchItem: '', search: '' });
  const [orderDirections, setOrderDirections] = useState({ item: '', direction: '' });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [statusValue, setStatusValue] = useState('');
  const [distributionStatusValue, setDistributionStatusValue] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    displayShortLinkDomains(page);
  }, [isOrdering, isSearching, page]);

  const handleSubmitSearch = searchBox => {
    setSearchField({ searchItem: searchBox.searchItem, search: searchBox.search });
    setIsSearching(searchBox.search);
  };

  const displayShortLinkDomains = async currentPage => {
    try {
      setIsLoading(true);
      const params = isSearching ? { searchTerm: searchField.search, searchItem: searchField.searchItem } : {};
      const { data } = await getShortLinkDomains(currentPage || page, params.searchTerm, params.searchItem);
      setShortLinkDomains(data.rows);
      setTotalItems(data.count);
      setTotalPages(data.totalPages);
      setPage(data.page);
      setIsLoading(false);
    } catch (error) {
      dispatch(showAlert('error', error.message));
      setIsLoading(false);
    }
  };

  const openUpdateModal = item => {
    setSelectedItem(item);
    setStatusValue(item.status || '');
    setDistributionStatusValue(item.distributionStatus || '');
    setIsModalOpen(true);
  };

  const handleUpdate = async () => {
    try {
      setIsUpdating(true);
      await updateShortLinkDomain(selectedItem.id, selectedItem.channelUid, {
        status: statusValue,
        distributionStatus: distributionStatusValue,
      });
      dispatch(showAlert('success', 'Short link domain updated successfully'));
      setIsModalOpen(false);
      displayShortLinkDomains(page);
    } catch (error) {
      dispatch(showAlert('error', error.message));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async item => {
    try {
      await deleteShortLinkDomain(item.id, item.channelUid);
      dispatch(showAlert('success', 'Short link domain deleted successfully'));
      displayShortLinkDomains(page);
    } catch (error) {
      dispatch(showAlert('error', error.message));
    }
  };

  if (isLoading) return <CircularProgress color="inherit" className="circular-progress" />;

  return (
    <>
      <div className="users-container">
        <h1>Short Link Domains ({totalItems})</h1>

        <SearchBox
          selectItems={['id', 'channelUid', 'domain']}
          onSubmitSearch={handleSubmitSearch}
          isSearching={isSearching}
          setIsOrdering={setIsOrdering}
        />
      </div>

      <DataTable
        useUid={true}
        head={headers}
        body={shortLinkDomains}
        action={false}
        bodyColumn={headers}
        linkPath={'short-link-domains'}
        totalPages={totalPages}
        currentPage={page}
        orderDirections={orderDirections}
        setOrderDirections={setOrderDirections}
        setPageChange={setPage}
        setIsOrdering={setIsOrdering}
        isOrdering={isOrdering}
        customActions={[
          {
            icon: <EditIcon />,
            action: item => openUpdateModal(item),
          },
          {
            icon: <TrashCanIcon />,
            action: item => handleDelete(item),
          },
        ]}
      />

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <Text tag="h2" type="subtitle" size={20} weight="semibold">
          Update Short Link Domain
        </Text>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <InputField
            label="Status"
            value={statusValue}
            onChange={e => setStatusValue(e.target.value)}
            placeholder="Enter status"
          />

          <InputField
            label="Distribution Status"
            value={distributionStatusValue}
            onChange={e => setDistributionStatusValue(e.target.value)}
            placeholder="Enter distribution status"
          />
        </div>

        <Button fullWidth appearence="solid" mt="16px" isLoading={isUpdating} onClick={handleUpdate}>
          Update
        </Button>
      </Modal>
    </>
  );
};

export default ShortLinkDomains;
